/**
 * error-humanize.ts
 *
 * Converts raw Error / unknown values into user-friendly copy.
 * Used wherever the app would otherwise render `error.message` directly.
 */

import { getLocale } from '@/i18n';

export interface HumanizedError {
  title: string;
  description: string;
  technical?: string;
}

export function humanizeError(err: unknown): HumanizedError {
  const en = getLocale() === 'en';
  if (err instanceof Error) {
    const msg = err.message;
    if (/network|fetch|failed to fetch|timeout/i.test(msg)) {
      return {
        title: en ? 'No connection right now' : 'Sem conexão agora',
        description: en ? 'Check your internet and try again' : 'Verifica a internet e tenta de novo',
        technical: msg,
      };
    }
    if (/401|unauthorized|session|token/i.test(msg)) {
      return {
        title: en ? 'Your session expired' : 'Sua sessão expirou',
        description: en ? 'Please log in again' : 'Faça login novamente',
        technical: msg,
      };
    }
    if (/403|forbidden/i.test(msg)) {
      return {
        title: en ? 'Access denied' : 'Acesso negado',
        description: en ? "You don't have permission for this action" : 'Você não tem permissão pra esta ação',
        technical: msg,
      };
    }
    if (/404|not.?found/i.test(msg)) {
      return {
        title: en ? "We couldn't find that" : 'Não encontramos isso',
        description: en ? 'The item may have been removed' : 'O item pode ter sido removido',
        technical: msg,
      };
    }
    if (/422|unprocessable/i.test(msg)) {
      return {
        title: en ? 'Invalid data' : 'Dados inválidos',
        description: en ? 'Check the information and try again' : 'Verifique as informações e tente de novo',
        technical: msg,
      };
    }
    if (/500|server|internal/i.test(msg)) {
      return {
        title: en ? 'Server error' : 'Erro no servidor',
        description: en ? "We're looking into it. Please try again shortly." : 'Estamos investigando. Tente novamente em instantes',
        technical: msg,
      };
    }
    return {
      title: en ? 'Something went wrong' : 'Algo deu errado',
      description: en ? 'Try reloading the page' : 'Tente recarregar a página',
      technical: msg,
    };
  }
  return {
    title: en ? 'Something went wrong' : 'Algo deu errado',
    description: en ? 'Try reloading' : 'Tente recarregar',
    technical: String(err),
  };
}

/**
 * 2026-05-20 · Tiny single-line humanizer for raw HTTP status codes.
 *
 * Use when the call-site just wants a short message to surface to the
 * PM (e.g. inside a thrown Error, a toast, or a setError state). For richer
 * title/description card layouts prefer `humanizeError` above.
 *
 * The Error message used to be `http_${status}` literally — pure jargon for
 * the user. These mappings cover the four categories that ever actually
 * happen for our R2/Worker endpoints (BIM file fetches, agent voice/chat).
 */
export function humanizeHttpStatus(status: number): string {
  const en = getLocale() === 'en';
  if (status === 401) return en ? 'Your session expired. Please log in again.' : 'Sua sessão expirou. Faz login de novo.';
  if (status === 403) return en ? 'No permission for this file.' : 'Sem permissão pra esse arquivo.';
  if (status === 404) return en ? 'File not found.' : 'Arquivo não encontrado.';
  if (status >= 500 && status < 600) {
    return en ? 'Server issue, try again in a few seconds.' : 'Problema no servidor, tenta de novo em alguns segundos.';
  }
  // 4xx fallthrough (408, 429, etc.) — keep generic, no raw code.
  return en ? "Couldn't load right now. Try again in a moment." : 'Não consegui carregar agora. Tenta de novo em instantes.';
}
